import { FieldError, FieldErrors, UseFormRegister } from 'react-hook-form';

import React from 'react';

type FormFieldError = FieldError | undefined | FieldErrors<any>;

interface InputFieldProps {
  name: string;
  label?: string;
  description?: string;
  type: string;
  placeholder?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  register?: UseFormRegister<any>;
  error?: FormFieldError;
  className?: string;
  icon?: string;
  iconAlt?: string;
  unitDescription?: string | React.ReactNode;
  size?: string;
  autoComplete?: string;
  min?: number;
  max?: number;
  inputClassName?: string;
}

const InputField: React.FC<InputFieldProps> = ({
  name,
  label,
  description,
  type,
  placeholder,
  value,
  onChange,
  onBlur,
  disabled = false,
  register,
  error,
  className,
  icon,
  iconAlt = '',
  unitDescription,
  size = '',
  autoComplete,
  min,
  max,
  inputClassName = ''
}) => {
  const inputProps = register
    ? {
        ...register(name),
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          // Trim whitespace for email type inputs
          if (type === 'email') {
            e.target.value = e.target.value.trim();
          }
          // Handle number type inputs
          if (type === 'number') {
            const value = e.target.value;
            // Allow empty string to prevent showing 0
            if (value === '') {
              // Don't modify the value, let it be empty
            } else if (value !== '') {
              const numValue = parseFloat(value);
              // Validate min/max constraints
              if (min !== undefined && numValue < min) {
                e.target.value = min.toString();
              } else if (max !== undefined && numValue > max) {
                e.target.value = max.toString();
              }
            }
          }
          register(name).onChange(e);
        },
        onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
          register(name).onBlur(e);
          if (onBlur) onBlur(e);
        }
      }
    : {
        value: value || '',
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          // Handle number type inputs for non-register usage
          if (type === 'number') {
            const value = e.target.value;
            // Allow empty string to prevent showing 0
            if (value === '') {
              // Don't modify the value, let it be empty
            } else if (value !== '') {
              const numValue = parseFloat(value);
              // Validate min/max constraints
              if (min !== undefined && numValue < min) {
                e.target.value = min.toString();
              } else if (max !== undefined && numValue > max) {
                e.target.value = max.toString();
              }
            }
          }
          if (onChange) onChange(e);
        },
        onBlur: onBlur
      };

  const errorMessage = error && typeof error === 'object' && 'message' in error ? error.message : '';
  const hasIcon = !!icon;
  const hasUnitDescription = unitDescription != null && unitDescription !== '';
  const suffixPadding = hasIcon && hasUnitDescription ? 'pr-24' : hasUnitDescription ? 'pr-20' : hasIcon ? 'pr-12' : '';

  return (
    <div className={`${className ? className : ''}`}>
      {label && (
        <label
          className={`inline-block text-[#495057] font-medium text-xs  ${size === 'sm' ? 'md:mb-[0.45rem] md:text-sm' : 'md:text-lg mb-2'}`}
        >
          {label}
        </label>
      )}
      <div className="relative">
        <input
          className={`bg-lightGray rounded-[20px] border w-full font-medium text-sm md:text-base p-[0.585rem] ${size === 'sm' ? 'md:p-[0.45rem] md:px-[0.875rem] md:text-base' : 'md:p-[1.215rem]'}  px-4
            outline-none placeholder:text-[#93999F] text-[#495057]
            disabled:cursor-not-allowed disabled:placeholder:text-[#93999f91] disabled:opacity-50
            focus:outline-none focus:border-[#ADB5BD]
            ${error ? '!border-red-500' : ''}
            ${suffixPadding}
            ${inputClassName}
          `}
          disabled={disabled}
          type={type}
          placeholder={placeholder}
          autoComplete={autoComplete}
          min={type === 'number' ? min : undefined}
          max={type === 'number' ? max : undefined}
          onWheel={(e) => {
            // Prevent scrolling from changing number input values
            if (type === 'number') {
              e.currentTarget.blur();
            }
          }}
          onKeyDown={(e) => {
            // Prevent space key for email type inputs
            if (type === 'email' && e.key === ' ') {
              e.preventDefault();
            }
            // Handle number type inputs
            if (type === 'number') {
              // Allow navigation and control keys
              if (
                [
                  'Backspace',
                  'Delete',
                  'Tab',
                  'Enter',
                  'ArrowLeft',
                  'ArrowRight',
                  'ArrowUp',
                  'ArrowDown',
                  'Home',
                  'End'
                ].includes(e.key)
              ) {
                return;
              }
              // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
              if (e.ctrlKey && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) {
                return;
              }
              // Handle minus key - prevent if min is 0 or greater
              if (e.key === '-' && min !== undefined && min >= 0) {
                e.preventDefault();
                return;
              }
              // Prevent non-numeric characters
              if (!/[\d\-\+\.]/.test(e.key)) {
                e.preventDefault();
              }
            }
          }}
          {...inputProps}
        />
        {hasIcon && (
          <div className={`bg-lightGray absolute right-4 top-1/2 -translate-y-1/2 ${disabled ? 'opacity-50' : ''}`}>
            <img src={icon} alt={iconAlt} className="w-[12px] h-[16px]" />
          </div>
        )}
        {hasUnitDescription && (
          <div
            className={`bg-lightGray absolute top-1/2 -translate-y-1/2 text-sm font-medium text-[#495057] whitespace-nowrap ${hasIcon ? 'right-10' : 'right-4'} ${disabled ? 'opacity-50' : ''}`}
          >
            {unitDescription}
          </div>
        )}
      </div>
      {(errorMessage || description) && (
        <p className={`mt-2 text-xs ${errorMessage ? 'text-red-600' : 'text-[#93999F]'}`}>
          {(errorMessage as String) || description}
        </p>
      )}
    </div>
  );
};

export default InputField;
